import { useState } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import { Checkbox, Collapse, Icon, IconButton, Switch } from '@atom/mui';
import {
  KeyboardArrowDownIcon,
  KeyboardArrowUpIcon,
  WorkIcon,
  WorkOutlineIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { toggleFromSet } from '@atom/utilities/setUtilities';
import {
  ASSIGNED_STATUS_ID,
  CLOSED_STATUS_ID,
  COMPLETED_STATUS_ID,
  IN_PROGRESS_STATUS_ID,
  IN_REVIEW_STATUS_ID,
  REQUESTED_STATUS_ID,
  UNASSIGNED_STATUS_ID,
} from '@atom/utilities/workOrderStatusUtilities';

import { useMapLayers } from '../MapPortalDataProvider';

import TemplateFolderFilter from './TemplateFolderFilter';
import WorkTemplateFilter from './WorkTemplateFilter';

import classes from './filterPanel.module.css';

type WorkStatus = {
  id: string;
  label: string;
  color: string;
};

const STATUS: WorkStatus[] = [
  {
    id: REQUESTED_STATUS_ID,
    label: 'Requested',
    color: colors.status.requested,
  },
  {
    id: UNASSIGNED_STATUS_ID,
    label: 'Unassigned',
    color: colors.status.unassigned,
  },
  {
    id: ASSIGNED_STATUS_ID,
    label: 'Assigned',
    color: colors.status.assigned,
  },
  {
    id: IN_PROGRESS_STATUS_ID,
    label: 'In Progress',
    color: colors.status.inprogress,
  },
  {
    id: COMPLETED_STATUS_ID,
    label: 'Completed',
    color: colors.status.completed,
  },
  {
    id: IN_REVIEW_STATUS_ID,
    label: 'In Review',
    color: colors.status.inReview,
  },
  {
    id: CLOSED_STATUS_ID,
    label: 'Closed',
    color: colors.neutral.dim,
  },
];

const STATUS_ROWS: WorkStatus[][] = R.splitEvery(2, STATUS);
const ALL_WORK_STATUS_IDS = STATUS.map(({ id }) => id);

const WorkSection = () => {
  const [expanded, setExpanded] = useState<boolean>(true);
  const { layers, setLayer, setFilter } = useMapLayers();
  const {
    work: toggled,
    workStatusIds,
    workTemplateIds,
    workTemplateFolderIds,
  } = layers;

  const toggleStatus = (statusId: string) => {
    setFilter('workStatusIds', toggleFromSet(workStatusIds, statusId));
  };

  const selectAll = () => {
    setFilter('workStatusIds', new Set(ALL_WORK_STATUS_IDS));
  };

  const clearAll = () => {
    setFilter('workStatusIds', new Set());
  };

  const allSelected = ALL_WORK_STATUS_IDS.every(id => workStatusIds.has(id));
  const noneSelected = workStatusIds.size === 0;

  return (
    <div className={classes.sectionContainer}>
      <div className={classes.sectionHeaderContainer}>
        <Switch
          label={
            <div className={classes.toggleLabel}>
              <div className={classes.sectionHeaderIcon}>
                <Icon icon={WorkOutlineIcon} />
              </div>
              <div>Work</div>
            </div>
          }
          checked={toggled}
          onChange={() => setLayer('work', !toggled)}
          data-cy="dashboardWorkToggle"
        />
        <IconButton
          edge="end"
          onClick={() => setExpanded(!expanded)}
          data-cy="dashboardWorkExpand"
        >
          <Icon icon={expanded ? KeyboardArrowUpIcon : KeyboardArrowDownIcon} />
        </IconButton>
      </div>
      <div className={classes.sectionFilters}>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <div className={classes.sectionLabel}>Status</div>
          <div className={classes.selectAllContainer}>
            <div
              onClick={() => toggled && !allSelected && selectAll()}
              className={clsx(
                classes.selectAllLabel,
                (!toggled || allSelected) && classes.disabled,
              )}
              data-cy="dashboardWorkSelectAll"
            >
              Select All
            </div>
            <div className={classes.selectAllDivider} />
            <div
              onClick={() => toggled && !noneSelected && clearAll()}
              className={clsx(
                classes.selectAllLabel,
                (!toggled || noneSelected) && classes.disabled,
              )}
              data-cy="dashboardWorkClearAll"
            >
              Clear All
            </div>
          </div>
          {STATUS_ROWS.map((pair, rowIndex) => (
            <div className={classes.sectionFilterRow} key={rowIndex}>
              {pair.map(status => (
                <div className={classes.sectionFilterCol} key={status.id}>
                  <Checkbox
                    edge="start"
                    checked={workStatusIds.has(status.id)}
                    onChange={() => toggleStatus(status.id)}
                    disabled={!toggled}
                    data-cy={`dashboardWorkFilter-${status.label.replace(/\s/g, '')}`}
                  />
                  <Icon icon={WorkIcon} color={status.color} />
                  <span className={classes.sectionFilterRowText}>
                    {status.label}
                  </span>
                </div>
              ))}
            </div>
          ))}
          <div className={classes.filterContainer}>
            <TemplateFolderFilter
              workTemplateFolderIds={workTemplateFolderIds}
              toggled={toggled}
              onWorkTemplateFoldersUpdate={ids =>
                setFilter('workTemplateFolderIds', ids)
              }
            />
          </div>
          <div className={classes.filterContainer}>
            <WorkTemplateFilter
              workTemplateIds={workTemplateIds}
              toggled={toggled}
              onWorkTemplatesUpdate={ids => setFilter('workTemplateIds', ids)}
            />
          </div>
        </Collapse>
      </div>
    </div>
  );
};

export default WorkSection;
